import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { PendingIcon } from "@images/svgIcons/pendingIcon"
import { BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffStatus } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffs } from "@modules/boxStaff/hooks/useBoxStaff"
import { useBoxStaffSidebar } from "@modules/boxStaff/hooks/useBoxStaffSidebar"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { UserCell } from "@modules/members/components/userCell"
import { DataTable, renderTableColumns } from "@modules/ui/components/dataTables/datatable"
import { HuNote } from "@modules/ui/components/huNote"
import { HUText } from "@modules/ui/components/huText"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { Column, ColumnProps } from "primereact/column"
import { DataTableStateEvent, DataTableValue } from "primereact/datatable"
import { Skeleton } from "primereact/skeleton"
import { useEffect, useState } from "react"
import { DefaultTheme, useTheme } from "styled-components"

type StaffColumnKey = "name" | "role" | "staffSince" | "status" | "note"

type Props = {
  diplayedColumns?: StaffColumnKey[]
  headerRightView?: React.JSX.Element
  columns?: ColumnProps[]
  page: number
  setPage: (p: number) => void
  size: number
  setSize: (s: number) => void
  hideHeaderOnEmptyLoading?: boolean
  empty: {
    full?: boolean
    view: React.ReactNode
  }
  setCountTab: (count: number) => void
  nameFilter: string
} & React.HTMLAttributes<HTMLDivElement>

export const DEFAULT_PAGE_SIZE = 25

export const BoxStaffTable: React.FC<Props> = ({
  diplayedColumns,
  columns,
  page,
  setPage,
  size,
  setSize,
  empty,
  nameFilter,
  setCountTab,
}) => {
  const displayedColumnsDefault: StaffColumnKey[] = ["name", "role", "staffSince", "status", "note"]
  const theme = useTheme()
  const language = useLanguage()
  const [selectedStaffMember, setselectedStaffMember] = useState<DataTableValue>()
  const { navigateToStaffDetailsInfo, navigateToStaffDetailsPending } = useBoxStaffSidebar()
  const { data, isLoading } = useBoxStaffs(page, size, nameFilter)

  useEffect(() => {
    if (data && data.content.length) {
      setCountTab(data.content.length)
    } else setCountTab(0)
  }, [data])

  const headerColumn = (key: StaffColumnKey) => {
    switch (key) {
      case "name":
        return word("box.staff.table.label.name")
      case "role":
        return word("box.staff.table.label.role")
      case "staffSince":
        return word("box.staff.table.label.staffSince")
      // case "assignedAthlete":
      //   return word("box.staff.table.label.assignedAthlete")
      case "status":
        return word("box.staff.table.label.status")
      case "note":
        return word("box.member.table.note")
      default:
        return ""
    }
  }

  const valueColumn = (key: StaffColumnKey, staff: BoxStaffItemEntity) => {
    switch (key) {
      case "name":
        return (
          <UserCell
            member={{
              avatar: staff.avatar,
              firstname: staff.firstname,
              lastname: staff.lastname,
            }}
            subtitle={staff.getUserName()}
            subtitleColor={theme.neutralColor500}
          />
        )
      case "role":
        return (
          <HUText color={theme.mainWhite} fontStyle="TS">
            {displayRole(staff.role)}
          </HUText>
        )
      case "staffSince":
        return (
          <HUText color={theme.mainWhite} fontStyle="BM">
            {formatDateWithHyphen(dayjs(staff.creationDate).format("L"))}
          </HUText>
        )
      // case "assignedAthlete":
      //   return (
      //     <HUText color={theme.mainWhite} fontStyle="BM">
      //       18
      //     </HUText>
      //   )
      case "status":
        if (staff.status === BoxStaffStatus.ACCEPTED) {
          return (
            <HUText color={theme.mainWhite} fontStyle="BM" className="flex gap-2 align-items-center">
              <CheckmarkIcon
                color={theme.sucess}
                style={{
                  width: "24px",
                  height: "24px",
                }}
              />{" "}
              {word("box.staff.table.status.active")}
            </HUText>
          )
        } else if (staff.status === BoxStaffStatus.PENDING) {
          return (
            <HUText color={theme.mainWhite} fontStyle="BM" className="flex gap-2 align-items-center">
              <PendingIcon
                color={theme.warning}
                style={{
                  width: "24px",
                  height: "24px",
                }}
              />{" "}
              {word("box.staff.table.status.pending")}
            </HUText>
          )
        } else return <></>

      case "note":
        return <HuNote id={staff.id} index={staff.id} note={staff.internalNote ? staff.internalNote : ""} />
    }
  }

  const onPage = (event: DataTableStateEvent) => {
    setPage(event.page ?? 0)
    setSize(event.rows)
  }

  const onSelectedStaffMember = (e: BoxStaffItemEntity) => {
    if (e.status === BoxStaffStatus.PENDING) {
      navigateToStaffDetailsPending(e.id, {
        onHide: () => {
          setselectedStaffMember(undefined)
          return Promise.resolve(true)
        },
        replaceAllComponents: true,
      })
    } else {
      navigateToStaffDetailsInfo(e.id, {
        onHide: () => {
          setselectedStaffMember(undefined)
          return Promise.resolve(true)
        },
        replaceAllComponents: true,
      })
    }
    setselectedStaffMember(e)
  }

  // SKELETON
  if (isLoading) {
    return (
      <div>
        <Skeleton width="100%" height="45rem" className="rounded-33" />
      </div>
    )
  }

  return (
    <div className="flex flex-column px-4">
      {!data || data.content.length <= 0 ? (
        empty.view
      ) : (
        <DataTable
          value={data.content as DataTableValue[]}
          dataKey="id"
          paginator
          lazy
          onPage={onPage}
          first={size * page}
          totalRecords={data.content.length}
          rows={size}
          currentPageReportTemplate={""}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage={empty.view}
          loading={isLoading}
          selectionMode="single"
          selection={selectedStaffMember}
          onSelectionChange={(e) => onSelectedStaffMember(e.value as BoxStaffItemEntity)}
          unstyled={undefined}
          metaKeySelection={true}
        >
          {renderTableColumns(
            diplayedColumns ?? displayedColumnsDefault,
            headerColumn,
            valueColumn,
            theme as DefaultTheme,
            language,
          )}
          {columns?.map((columnProps, index) => <Column key={"more-column-" + index} {...columnProps} />)}
        </DataTable>
      )}
    </div>
  )
}
